import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { userApi, emailApi } from "../utils/api-axios";
import { io } from "socket.io-client";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);

  // Use ref to prevent multiple connections
  const socketRef = useRef(null);
  const isConnectingRef = useRef(false);

  // Check if user is already logged in on app load
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        setLoading(false);
        return;
      }

      await fetchUserProfile();
    };

    initializeAuth();
  }, []);

  // Cleanup socket on unmount
  useEffect(() => {
    return () => {
      if (socketRef.current) {
        console.log("Cleaning up socket connection on unmount");
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  // FIXED: Better socket connection management
  const connectSocket = (userData) => {
    if (!userData) {
      console.log("No userData provided to connectSocket");
      return;
    }

    const userId = userData.id || userData._id;

    // Prevent multiple connections
    if (isConnectingRef.current) {
      console.log("Already connecting socket, skipping");
      return;
    }

    if (socketRef.current && socketRef.current.connected) {
      console.log("Socket already connected, skipping");
      return;
    }

    console.log("Connecting socket for user:", userId);
    isConnectingRef.current = true;

    // Clean up existing socket
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
    }

    // Fixed: Use correct environment variable name
    const socketUrl =
      process.env.REACT_APP_SOCKET_SERVER_URL || "http://localhost:4000";
    console.log("Connecting to socket server:", socketUrl);

    const newSocket = io(socketUrl, {
      auth: {
        userId: userId,
      },
      autoConnect: true,
      forceNew: false, // Don't force new connection unless necessary
      transports: ["websocket", "polling"], // Ensure both transports are available
      timeout: 20000, // 20 second timeout
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    newSocket.on("connect", () => {
      console.log("Socket connected successfully:", newSocket.id);
      console.log("Socket auth:", newSocket.auth);
      isConnectingRef.current = false;
    });

    newSocket.on("disconnect", (reason) => {
      console.log("Socket disconnected:", reason);
      isConnectingRef.current = false;

      // Auto-reconnect for certain disconnect reasons
      if (reason === "io server disconnect") {
        console.log("Server disconnected, will auto-reconnect");
      }
    });

    newSocket.on("getOnlineUsers", (userIds) => {
      console.log("Online users updated:", userIds);
      setOnlineUsers(userIds);
    });

    newSocket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
      console.error("Socket URL attempted:", socketUrl);
      isConnectingRef.current = false;
    });

    // Handle ban events from socket
    newSocket.on("banned", (banData) => {
      console.log("Received ban event from socket:", banData);
      // Force logout
      logout();
    });

    // Add error handling for general socket errors
    newSocket.on("error", (error) => {
      console.error("Socket error:", error);
    });
  };

  const fetchUserProfile = async () => {
    try {
      const data = await userApi.getProfile();

      if (data.success === false) {
        throw new Error(data.message || "Authentication failed");
      }

      setIsAuthenticated(true);
      setCurrentUser(data.user || data);
      connectSocket(data.user || data);
    } catch (error) {
      console.error("Profile fetch error:", error);

      const errorMessage = error.message?.toLowerCase() || "";
      if (
        errorMessage.includes("unauthorized") ||
        errorMessage.includes("session expired") ||
        errorMessage.includes("authentication failed")
      ) {
        setIsAuthenticated(false);
        setCurrentUser(null);
        localStorage.removeItem("token");
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, remember = false) => {
    try {
      const data = await userApi.login(email, password, remember);

      if (data.success) {
        const token =
          typeof data.token === "string"
            ? data.token
            : JSON.stringify(data.token);
        localStorage.setItem("token", token);
        setIsAuthenticated(true);
        setCurrentUser(data.user);
        connectSocket(data.user);
        return { success: true, user: data.user };
      } else {
        // FIXED: Return all the data from the response, including ban info
        return {
          success: false,
          message: data.message,
          error: data.message || "Login failed. Please check your credentials.",
          banned: data.banned,
          banInfo: data.banInfo,
          requiresVerification: data.requiresVerification,
          email: data.email
        };
      }
    } catch (error) {
      console.error("Login error:", error);
      
      // FIXED: Extract ban information from error.data (set by axios interceptor)
      const errorData = error.data || error.response?.data || {};
      const statusCode = error.status || error.response?.status;
      
      return {
        success: false,
        message: errorData.message || error.message || "Network error. Please try again.",
        error: errorData.message || error.message || "Network error. Please try again.",
        banned: errorData.banned || (statusCode === 403),
        banInfo: errorData.banInfo,
        forceLogout: errorData.forceLogout,
        requiresVerification: errorData.requiresVerification,
        email: errorData.email
      };
    }
  };

  const register = async (userData) => {
    try {
      const data = await userApi.register(userData);

      if (data.success) {
        const token =
          typeof data.token === "string"
            ? data.token
            : JSON.stringify(data.token);
        localStorage.setItem("token", token);
        setIsAuthenticated(true);
        setCurrentUser(data.user);
        connectSocket(data.user);
        return { success: true, user: data.user };
      } else {
        return {
          success: false,
          error: data.message || "Registration failed. Please try again.",
          requiresVerification: data.requiresVerification,
          email: data.email
        };
      }
    } catch (error) {
      console.error("Registration error:", error);
      return {
        success: false,
        error: error.message || "Network error. Please try again.",
      };
    }
  };

  const forgotPassword = async (email) => {
    try {
      const data = await userApi.forgotPassword(email);

      if (data.success) {
        return {
          success: true,
          message: data.message || "Password reset email sent successfully",
        };
      } else {
        return {
          success: false,
          error: data.message || "Failed to send password reset email.",
        };
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      return {
        success: false,
        error: error.message || "Network error. Please try again.",
      };
    }
  };

  const resetPassword = async (token, password, confirmPassword) => {
    try {
      const data = await userApi.resetPassword(token, password, confirmPassword);

      if (data.success) {
        return {
          success: true,
          message: data.message || "Password has been reset successfully",
        };
      } else {
        return {
          success: false,
          error: data.message || "Failed to reset password.",
        };
      }
    } catch (error) {
      console.error("Reset password error:", error);
      return {
        success: false,
        error: error.message || "Network error. Please try again.",
      };
    }
  };

  // Email verification functions
  const verifyEmail = async (token) => {
    try {
      const data = await emailApi.verifyEmail(token);
      return {
        success: data.success,
        message: data.message,
        expired: data.expired || false,
      };
    } catch (error) {
      console.error("Email verification error:", error);
      return {
        success: false,
        message: error.message || "An error occurred during verification. Please try again.",
        expired: false,
      };
    }
  };

  const checkTokenForEmail = async (token) => {
    try {
      const data = await emailApi.checkToken(token);
      return {
        success: data.success,
        email: data.email || null,
      };
    } catch (error) {
      console.error("Token check error:", error);
      return {
        success: false,
        email: null,
      };
    }
  };

  const resendVerificationEmail = async (email) => {
    try {
      const data = await emailApi.resendVerification(email);
      return {
        success: data.success,
        message: data.message || "Verification email sent successfully",
      };
    } catch (error) {
      console.error("Resend verification error:", error);
      return {
        success: false,
        message: error.message || "Failed to resend verification email. Please try again.",
      };
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setCurrentUser(null);
    localStorage.removeItem("token");
    setOnlineUsers([]);

    if (socketRef.current) {
      console.log("Disconnecting socket on logout");
      socketRef.current.disconnect();
      socketRef.current = null;
      setSocket(null);
    }

    isConnectingRef.current = false;
  };

  const updateProfile = async (profileData) => {
    try {
      const data = await userApi.updateProfile(profileData);

      if (data.success) {
        setCurrentUser(data.user);
        return { success: true, user: data.user };
      } else {
        return {
          success: false,
          error: data.message || "Profile update failed.",
        };
      }
    } catch (error) {
      console.error("Profile update error:", error);
      return {
        success: false,
        error: error.message || "Network error. Please try again.",
      };
    }
  };

  const updateProfileImage = async (imageFile) => {
    try {
      const data = await userApi.updateProfileImage(imageFile);

      if (data.success) {
        setCurrentUser(data.user);
        return { success: true, user: data.user };
      } else {
        return {
          success: false,
          error: data.message || "Image update failed.",
        };
      }
    } catch (error) {
      console.error("Image update error:", error);
      return {
        success: false,
        error: error.message || "Network error. Please try again.",
      };
    }
  };

  const deleteAccount = async () => {
    try {
      const response = await userApi.deleteAccount();
      console.log(response);

      return response;
    } catch (error) {
      console.error("Error deleting account:", error);
      return { success: false, message: "Failed to delete account" };
    }
  };

  const changePassword = async (passwordData) => {
    try {
      const response = await userApi.updatePassword(passwordData);
      return response;
    } catch (error) {
      console.error("Password update error:", error);
      return { success: false, message: "Failed to update password." };
    }
  };

  const getUsername = () => {
    if (!currentUser) return "";
    return (
      `${currentUser.firstName} ${currentUser.lastName}` ||
      currentUser.email?.split("@")[0] ||
      "User"
    );
  };

  const getUserAvatar = () => {
    if (!currentUser) return undefined;
    return currentUser.image;
  };

  const getToken = () => {
    return localStorage.getItem("token");
  };

  // Re-fetch the current user from the backend. Use after a profile change
  // so currentUser reflects the latest server state.
  const refreshUser = async () => {
    await fetchUserProfile();
  };

  // Update just the user's location. Returns { success, error? }.
  const updateLocation = async (locationPayload) => {
    try {
      const data = await userApi.updateProfile({ location: locationPayload });
      if (data.success) {
        setCurrentUser(data.user);
        return { success: true, user: data.user };
      }
      return { success: false, error: data.message || "Update failed" };
    } catch (error) {
      console.error("Location update error:", error);
      return {
        success: false,
        error: error?.data?.message || error.message || "Network error",
      };
    }
  };

  const clearLocation = async () => updateLocation(null);

  const value = {
    isAuthenticated,
    currentUser,
    loading,
    socket,
    onlineUsers,
    login,
    register,
    logout,
    updateProfile,
    updateProfileImage,
    updateLocation,    // NEW
    clearLocation,     // NEW
    refreshUser,       // NEW
    getUsername,
    getUserAvatar,
    getToken,
    deleteAccount,
    changePassword,
    forgotPassword,
    resetPassword,
    // Email verification functions
    verifyEmail,
    checkTokenForEmail,
    resendVerificationEmail,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};